'use client';

/**
 * @brisa/ui · components/ui/dropdown-menu — shadcn DropdownMenu (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 · adicionado Bruno 2026-05-15
 *
 * Wrapper Radix UI dropdown-menu com estilo facelift:
 *   - Content: bg-white + rounded-[16px] + shadow-2xl (shadow-large)
 *   - Item: hover bg-text-08, active text-text-56 (Bruno 2026-05-15)
 *
 * Uso: tipicamente acoplado a um Button trigger (asChild). Ver
 * HeaderFacelift para exemplo canônico (seletor de idioma).
 *
 * 'use client' obrigatório — Radix DropdownMenu orquestra portal/state.
 */
import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { cn } from '../../lib/utils';

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;
export const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 8, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // facelift token: rounded-[16px], shadow-large (shadow-2xl), bg-white
        'z-50 min-w-[10rem] overflow-hidden rounded-[16px] border border-[hsl(var(--text-08))]',
        'bg-white p-2 text-[hsl(var(--text-100))] shadow-2xl',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    /** Quando true, item recebe text-56 (#000 56%) — selecionado/current. */
    active?: boolean;
  }
>(({ className, active, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm font-medium',
      'transition-colors outline-none',
      'focus:bg-[hsl(var(--text-08))] hover:bg-[hsl(var(--text-08))]',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      active && 'text-[hsl(var(--text-56))]',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--text-48))]', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[hsl(var(--text-08))]', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
